/**
 * Prospetto per emissione fattura passiva — STATELESS (pattern #190).
 *
 * «Ecco cosa dovete fatturarci»: il fornitore riceve il PDF fuori banda, qui
 * si sceglie cosa includere. Conferimenti del fornitore PRESELEZIONATI tutti;
 * blocco trasporto a tre stati (assente / a carico fornitore / a carico
 * Ellezeta), precompilato dai valori d'ordine SOLO se concordi fra i
 * conferimenti selezionati — se discordano i campi restano vuoti e lo si dice,
 * non si indovina.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, FileText } from 'lucide-react';
import Modal from '../ui/Modal';
import { openPdfInTab } from '../../utils/pdfPreview';
import type { DaRegistrareItem, ProspettoInput } from '../../api/fatturePassive';

const inputCls = 'text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage bg-white w-full';

type DirezioneUi = 'assente' | 'fornitore' | 'ellezeta';

interface Props {
  customerId: string;
  fornitoreNome: string;
  /** TUTTI i da-registrare del fornitore (non solo quelli spuntati in tabella). */
  items: DaRegistrareItem[];
  onClose: () => void;
}

/** Valore comune a tutti gli item, oppure null se discordano (o lista vuota). */
function concorde<T>(values: T[]): T | null {
  if (values.length === 0) return null;
  return values.every((v) => v === values[0]) ? values[0] : null;
}

export default function ProspettoPassivoModal({ customerId, fornitoreNome, items, onClose }: Props) {
  const { t } = useTranslation();
  // Preselezione: tutti i conferimenti del fornitore.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(items.map((i) => i.conferimento_id)),
  );
  const selectedItems = useMemo(
    () => items.filter((i) => selected.has(i.conferimento_id)),
    [items, selected],
  );

  // Prefill trasporto dai valori d'ordine dei SELEZIONATI, solo se concordi.
  const prefill = useMemo(() => {
    const costi = selectedItems.map((i) => i.costo_trasporto_eur_per_viaggio);
    const viaggi = selectedItems.map((i) => i.numero_trasporti_stimati);
    const porta = selectedItems.map((i) => i.cliente_porta_materiale);
    const costo = concorde(costi);
    const nViaggi = concorde(viaggi);
    const portaConcorde = concorde(porta);
    return {
      costo, nViaggi,
      // fornitore porta → trasporto a suo carico; Ellezeta ritira → a carico Ellezeta.
      direzione: (portaConcorde == null ? null : portaConcorde ? 'fornitore' : 'ellezeta') as DirezioneUi | null,
      discordanti: (costi.length > 0 && costo == null) || nViaggi == null || portaConcorde == null,
    };
  }, [selectedItems]);

  const [direzione, setDirezione] = useState<DirezioneUi>(prefill.direzione ?? 'assente');
  const [costo, setCosto] = useState(prefill.costo != null ? String(prefill.costo) : '');
  const [viaggi, setViaggi] = useState(prefill.nViaggi != null ? String(prefill.nViaggi) : '');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function genera() {
    setError(null);
    if (selected.size === 0) { setError(t('fatturazione.prospetto.err_no_selezione')); return; }
    const body: ProspettoInput = { customer_id: customerId, conferimento_ids: [...selected] };
    if (direzione !== 'assente') {
      const c = Number(costo.replace(',', '.'));
      const v = Number(viaggi);
      if (Number.isNaN(c) || c <= 0 || Number.isNaN(v) || v < 1 || !Number.isInteger(v)) {
        setError(t('fatturazione.prospetto.err_trasporto'));
        return;
      }
      body.trasporto = { direzione, costo_per_viaggio: c, numero_viaggi: v };
    }
    setGenerating(true);
    try {
      await openPdfInTab('/api/fatture-passive/prospetto-pdf', { method: 'POST', body });
      onClose();
    } catch {
      setError(t('fatturazione.prospetto.err_generazione'));
    } finally {
      setGenerating(false);
    }
  }

  return (
    <Modal
      title={t('fatturazione.prospetto.titolo', { fornitore: fornitoreNome })}
      onClose={onClose}
      testid="prospetto-passivo-modal"
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={generating}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
            {t('common.azioni.annulla')}
          </button>
          <button type="button" onClick={genera} disabled={generating || selected.size === 0}
            data-testid="btn-genera-prospetto"
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60 inline-flex items-center gap-1.5">
            <FileText size={14} />
            {generating ? t('fatturazione.prospetto.generazione') : t('fatturazione.prospetto.genera')}
          </button>
        </>
      )}
    >
      <p className="text-sm text-ink-mid">{t('fatturazione.prospetto.intro')}</p>

      {/* Conferimenti del fornitore, tutti preselezionati. */}
      <div className="border border-stone-mid rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-bg">
            <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
              <th className="px-3 py-2"></th>
              <th className="px-3 py-2">{t('fatturazione.prospetto.col_conferimento')}</th>
              <th className="px-3 py-2 text-right">{t('fatturazione.prospetto.col_peso')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.conferimento_id} className="border-t border-stone-mid/60">
                <td className="px-3 py-2">
                  <input type="checkbox" className="accent-sage"
                    data-testid={`prospetto-conf-${d.conferimento_id}`}
                    checked={selected.has(d.conferimento_id)}
                    onChange={() => toggle(d.conferimento_id)} />
                </td>
                <td className="px-3 py-2">{d.numero ?? d.codice_ordine}
                  {d.numero_fir && <span className="block text-[11px] text-ink-faint">{d.numero_fir}</span>}
                </td>
                <td className="px-3 py-2 text-right">{d.peso_netto_kg ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Blocco trasporto: tre stati, scelta esplicita. */}
      <div className="space-y-2">
        <label className="block text-xs font-medium text-ink-soft">{t('fatturazione.prospetto.trasporto_label')}</label>
        <select className={inputCls} data-testid="prospetto-trasporto-direzione"
          value={direzione} onChange={(e) => setDirezione(e.target.value as DirezioneUi)}>
          <option value="assente">{t('fatturazione.prospetto.trasp_assente')}</option>
          <option value="fornitore">{t('fatturazione.prospetto.trasp_fornitore')}</option>
          <option value="ellezeta">{t('fatturazione.prospetto.trasp_ellezeta')}</option>
        </select>
        {direzione !== 'assente' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-ink-soft mb-1">{t('fatturazione.prospetto.costo_viaggio')}</label>
              <input type="number" min="0" step="0.01" className={inputCls}
                data-testid="prospetto-costo-viaggio"
                value={costo} onChange={(e) => setCosto(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-ink-soft mb-1">{t('fatturazione.prospetto.numero_viaggi')}</label>
              <input type="number" min="1" step="1" className={inputCls}
                data-testid="prospetto-numero-viaggi"
                value={viaggi} onChange={(e) => setViaggi(e.target.value)} />
            </div>
          </div>
        )}
        {prefill.discordanti && (
          <p className="text-xs text-amber-700 flex items-start gap-1" data-testid="prospetto-discordanza">
            <AlertCircle size={14} className="mt-px flex-shrink-0" />
            {t('fatturazione.prospetto.valori_discordanti')}
          </p>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-700 flex items-start gap-1">
          <AlertCircle size={14} className="mt-px flex-shrink-0" />{error}
        </p>
      )}
    </Modal>
  );
}
